import React, { useEffect, useState, useContext } from 'react';
import {
  Table, Input, Modal, Form, message, Button, Breadcrumb,
  Tag, Tabs, InputNumber, Select, Tooltip, Space, Card, Statistic, Timeline, Switch
} from 'antd';
import {
  EditOutlined, RightOutlined, ReloadOutlined, HistoryOutlined,
  SettingOutlined, DollarOutlined, GiftOutlined, PercentageOutlined,
  InfoCircleOutlined, CheckOutlined, CloseOutlined, DeleteOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { settingsService, citiesService } from '../../common/services';
import { AuthContext } from '../../common/constants';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { TextArea } = Input;

const CATEGORY_CONFIG = {
  finance: { label: 'Finance', color: 'blue', icon: <DollarOutlined /> },
  bonus: { label: 'Bonus', color: 'green', icon: <GiftOutlined /> },
  fees: { label: 'Frais', color: 'orange', icon: <PercentageOutlined /> },
  system: { label: 'Système', color: 'purple', icon: <SettingOutlined /> },
  affiliates: { label: 'Affiliés', color: 'gold', icon: <GiftOutlined /> },
};

const Settings = () => {
  const { currentUser } = useContext(AuthContext);
  const [settings, setSettings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Villes
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Modal d'édition
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [form] = Form.useForm();

  // Modal d'historique
  const [isHistoryModalOpen, setHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Rôles avec accès complet
  const fullAccessRoles = ['superAdmin', 'directeur', 'support'];
  const hasFullAccess = fullAccessRoles.includes(currentUser?.role);

  const canUpdate = hasFullAccess || currentUser?.permissions?.includes('settings:update');
  const canCreate = hasFullAccess || currentUser?.permissions?.includes('settings:create');

  useEffect(() => {
    fetchSettings();
    fetchCategories();
    fetchCities();
  }, []);

  // Recharger les settings quand la ville change
  useEffect(() => {
    fetchSettings();
  }, [selectedCity]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsService.getAll(null, selectedCity);
      if (response?.success) {
        setSettings(response.data || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des paramètres:', err);
      message.error('Impossible de charger les paramètres');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await settingsService.getCategories();
      if (response?.success) {
        setCategories(response.data || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await citiesService.getAll();
      if (response?.success || Array.isArray(response?.data)) {
        setCities(response.data || response || []);
      } else if (Array.isArray(response)) {
        setCities(response);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des villes:', err);
    }
  };

  const handleSeed = async () => {
    try {
      const response = await settingsService.seed();
      if (response?.success) {
        message.success(`Paramètres initialisés: ${response.data.created.length} créés, ${response.data.skipped.length} ignorés`);
        fetchSettings();
        fetchCategories();
      }
    } catch (err) {
      message.error('Erreur lors de l\'initialisation');
    }
  };

  const handleRefreshCache = async () => {
    try {
      const response = await settingsService.refreshCache();
      if (response?.success) {
        message.success('Cache rafraîchi avec succès');
      }
    } catch (err) {
      message.error('Erreur lors du rafraîchissement du cache');
    }
  };

  const handleEdit = (record) => {
    setSelectedSetting(record);
    // Si une ville est sélectionnée et qu'un override existe, préremplir avec l'override
    const initialValue = selectedCity && record.hasOverride
      ? record.resolvedValue
      : selectedCity
        ? record.value // Pas d'override, afficher la valeur par défaut
        : record.value;
    form.setFieldsValue({
      value: initialValue,
      reason: '',
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedSetting) return;

      const response = await settingsService.update(
        selectedSetting.key,
        values.value,
        values.reason || '',
        selectedCity || null
      );

      if (response?.success) {
        message.success(response.message || 'Paramètre mis à jour avec succès');
        setEditModalOpen(false);
        fetchSettings();
      } else {
        message.error(response?.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur de mise à jour:', error);
      message.error(error?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleRemoveCityOverride = async () => {
    if (!selectedSetting || !selectedCity) return;

    Modal.confirm({
      title: 'Supprimer l\'override',
      content: `Voulez-vous supprimer l'override pour cette ville ? Le paramètre utilisera la valeur par défaut.`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await settingsService.removeCityOverride(
            selectedSetting.key,
            selectedCity,
            'Suppression depuis le backoffice'
          );
          if (response?.success) {
            message.success(response.message || 'Override supprimé');
            setEditModalOpen(false);
            fetchSettings();
          } else {
            message.error(response?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          message.error(error?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const handleViewHistory = async (record) => {
    setHistoryLoading(true);
    setHistoryModalOpen(true);
    try {
      const response = await settingsService.getHistory(record.key, 50, 0, selectedCity);
      if (response?.success) {
        setHistoryData(response.data);
      }
    } catch (err) {
      message.error('Erreur lors du chargement de l\'historique');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      const response = await settingsService.toggle(record.key, checked);
      if (response?.success) {
        message.success(checked ? 'Paramètre activé' : 'Paramètre désactivé');
        fetchSettings();
      } else {
        message.error(response?.message || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur de toggle:', error);
      message.error(error?.message || 'Erreur lors de la modification');
    }
  };

  const filteredSettings = settings.filter((setting) => {
    const matchesCategory = activeCategory === 'all' || setting.category === activeCategory;
    const matchesSearch =
      setting.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.label.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatValue = (value, valueType, unit) => {
    if (value === undefined || value === null) return '-';
    if (valueType === 'percentage') {
      return `${(value * 100).toFixed(2)}%`;
    }
    if (unit === 'points' || unit === 'FCFA') {
      return `${value.toLocaleString()} ${unit}`;
    }
    if (valueType === 'string') {
      const str = String(value);
      return str.length > 50 ? str.substring(0, 50) + '...' : str;
    }
    return String(value);
  };

  // Nom de la ville sélectionnée
  const selectedCityName = selectedCity
    ? (cities.find(c => c.code === selectedCity)?.name || selectedCity.toUpperCase())
    : null;

  const columns = [
    {
      title: 'Paramètre',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.label}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            <code>{record.key}</code>
          </div>
        </div>
      ),
      sorter: (a, b) => a.label.localeCompare(b.label),
    },
    {
      title: 'Valeur',
      key: 'value',
      render: (_, record) => {
        if (selectedCity) {
          // Mode ville : afficher resolvedValue + tag Override/Défaut
          const displayValue = record.resolvedValue !== undefined ? record.resolvedValue : record.value;
          return (
            <Space>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>
                {formatValue(displayValue, record.valueType, record.unit)}
              </span>
              {record.hasOverride ? (
                <Tag color="green" style={{ fontSize: '11px' }}>Override</Tag>
              ) : (
                <Tag color="default" style={{ fontSize: '11px' }}>Défaut</Tag>
              )}
            </Space>
          );
        }
        // Mode normal : afficher value + nombre d'overrides
        return (
          <Space>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>
              {formatValue(record.value, record.valueType, record.unit)}
            </span>
            {record.overrideCount > 0 && (
              <Tooltip title={`${record.overrideCount} override(s) ville`}>
                <Tag color="blue" style={{ fontSize: '11px' }}>
                  <GlobalOutlined /> {record.overrideCount}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Villes',
      key: 'cities',
      width: 140,
      align: 'center',
      render: (_, record) => {
        const overrides = record.cityOverrides || {};
        const overrideCodes = Object.keys(overrides);
        if (overrideCodes.length === 0) {
          return <Tag color="default">Défaut global</Tag>;
        }
        // Résoudre les codes villes en noms
        const overrideDetails = overrideCodes.map(code => {
          const city = cities.find(c => c.code === code);
          const cityName = city ? city.name : code.toUpperCase();
          const val = overrides[code];
          return `${cityName}: ${formatValue(val, record.valueType, record.unit)}`;
        });
        return (
          <Tooltip title={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Overrides par ville :</div>
              {overrideDetails.map((detail, i) => (
                <div key={i}>{detail}</div>
              ))}
            </div>
          }>
            <Space size={2} wrap>
              {overrideCodes.map(code => {
                const city = cities.find(c => c.code === code);
                return (
                  <Tag key={code} color="blue" style={{ fontSize: 11, margin: 1 }}>
                    {city ? city.name : code.toUpperCase()}
                  </Tag>
                );
              })}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        const config = CATEGORY_CONFIG[category] || { label: category, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: Object.entries(CATEGORY_CONFIG).map(([key, val]) => ({
        text: val.label,
        value: key,
      })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ color: '#666' }}>{text || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Dernière modification',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tooltip title={record.isEnabled !== false ? 'Activé - Cliquer pour désactiver' : 'Désactivé - Cliquer pour activer'}>
          <Switch
            checked={record.isEnabled !== false}
            onChange={(checked) => handleToggle(record, checked)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            disabled={!canUpdate}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canUpdate && record.isEditable !== false && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                style={{ color: '#faad14' }}
              />
            </Tooltip>
          )}
          <Tooltip title="Historique">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleViewHistory(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const renderValueInput = () => {
    if (!selectedSetting) return <Input />;

    const { valueType, minValue, maxValue, unit } = selectedSetting;

    if (valueType === 'boolean') {
      return (
        <Select>
          <Select.Option value={true}>Oui</Select.Option>
          <Select.Option value={false}>Non</Select.Option>
        </Select>
      );
    }

    if (valueType === 'percentage') {
      return (
        <InputNumber
          min={minValue}
          max={maxValue}
          step={0.001}
          style={{ width: '100%' }}
          addonAfter="(décimal, ex: 0.015 = 1.5%)"
        />
      );
    }

    if (valueType === 'number' || valueType === 'integer') {
      return (
        <InputNumber
          min={minValue}
          max={maxValue}
          step={valueType === 'integer' ? 1 : 0.01}
          style={{ width: '100%' }}
          addonAfter={unit}
        />
      );
    }

    if (valueType === 'string') {
      return <TextArea rows={4} placeholder="Entrez le texte..." />;
    }

    return <Input />;
  };

  return (
    <>
      <Breadcrumb separator={<RightOutlined />}>
        <Breadcrumb.Item>
          <Link to="/dashboard">Accueil</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span>Administration</span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span>Paramètres système</span>
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', marginTop: '16px', flexWrap: 'wrap' }}>
        {categories.map((cat) => {
          const config = CATEGORY_CONFIG[cat.key] || { label: cat.label, color: 'default', icon: <SettingOutlined /> };
          return (
            <Card
              key={cat.key}
              size="small"
              style={{
                minWidth: '150px',
                cursor: 'pointer',
                borderColor: activeCategory === cat.key ? '#1890ff' : undefined,
              }}
              onClick={() => setActiveCategory(cat.key === activeCategory ? 'all' : cat.key)}
            >
              <Statistic
                title={config.label}
                value={cat.count}
                prefix={config.icon}
                valueStyle={{ color: activeCategory === cat.key ? '#1890ff' : undefined }}
              />
            </Card>
          );
        })}
      </div>

      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <Space>
              <Input
                placeholder="Rechercher par nom ou clé..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 300 }}
                allowClear
                prefix={<InfoCircleOutlined style={{ color: '#bbb' }} />}
              />
              <Select
                placeholder="Toutes les villes (défaut)"
                value={selectedCity}
                onChange={(value) => setSelectedCity(value)}
                allowClear
                style={{ width: 220 }}
                suffixIcon={<GlobalOutlined />}
              >
                {cities
                  .filter(c => c.isActive !== false)
                  .map(city => (
                    <Select.Option key={city.code} value={city.code}>
                      {city.name} ({city.code?.toUpperCase()})
                    </Select.Option>
                  ))
                }
              </Select>
            </Space>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshCache}
              >
                Rafraîchir le cache
              </Button>
              <Button
                type="primary"
                onClick={handleSeed}
              >
                Initialiser les paramètres
              </Button>
            </Space>
          </div>

          {selectedCity && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', border: '1px solid #91d5ff' }}>
              <GlobalOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              Affichage des valeurs pour la ville : <strong>{selectedCityName}</strong>
              {' '}— Les valeurs marquées "Override" sont spécifiques à cette ville.
            </div>
          )}

          <Table
            columns={columns}
            dataSource={filteredSettings}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            loading={isLoading}
            size="middle"
          />
        </div>
      </div>

      {/* Modal d'édition */}
      <Modal
        title={
          <span>
            <EditOutlined style={{ marginRight: 8 }} />
            Modifier: {selectedSetting?.label}
            {selectedCity && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {selectedCityName}
              </Tag>
            )}
          </span>
        }
        open={isEditModalOpen}
        onCancel={() => setEditModalOpen(false)}
        footer={[
          selectedCity && selectedSetting?.hasOverride && (
            <Button
              key="removeOverride"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveCityOverride}
              style={{ float: 'left' }}
            >
              Supprimer l'override
            </Button>
          ),
          <Button key="cancel" onClick={() => setEditModalOpen(false)}>
            Annuler
          </Button>,
          <Button key="submit" type="primary" onClick={handleEditSubmit}>
            Enregistrer
          </Button>,
        ]}
        width={500}
      >
        {selectedSetting && (
          <Form form={form} layout="vertical">
            <div style={{ marginBottom: 16, padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
              <div><strong>Clé:</strong> <code>{selectedSetting.key}</code></div>
              <div><strong>Type:</strong> {selectedSetting.valueType}</div>
              <div>
                <strong>Valeur par défaut:</strong>{' '}
                {formatValue(selectedSetting.value, selectedSetting.valueType, selectedSetting.unit)}
              </div>
              {selectedCity && selectedSetting.hasOverride && (
                <div style={{ color: '#52c41a' }}>
                  <strong>Override {selectedCityName}:</strong>{' '}
                  {formatValue(selectedSetting.resolvedValue, selectedSetting.valueType, selectedSetting.unit)}
                </div>
              )}
              {selectedSetting.minValue !== null && (
                <div><strong>Min:</strong> {selectedSetting.minValue}</div>
              )}
              {selectedSetting.maxValue !== null && (
                <div><strong>Max:</strong> {selectedSetting.maxValue}</div>
              )}
            </div>

            <Form.Item
              label={selectedCity ? `Nouvelle valeur pour ${selectedCityName}` : 'Nouvelle valeur'}
              name="value"
              rules={[{ required: true, message: 'La valeur est requise' }]}
            >
              {renderValueInput()}
            </Form.Item>

            <Form.Item
              label="Raison de la modification (optionnel)"
              name="reason"
            >
              <TextArea rows={2} placeholder="Ex: Ajustement suite à la réunion du..." />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Modal d'historique */}
      <Modal
        title={
          <span>
            <HistoryOutlined style={{ marginRight: 8 }} />
            Historique: {historyData?.label}
            {selectedCity && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {selectedCityName}
              </Tag>
            )}
          </span>
        }
        open={isHistoryModalOpen}
        onCancel={() => {
          setHistoryModalOpen(false);
          setHistoryData(null);
        }}
        footer={null}
        width={600}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Chargement...</div>
        ) : historyData?.history?.length > 0 ? (
          <Timeline
            items={historyData.history.map((h, idx) => ({
              color: idx === 0 ? 'green' : 'gray',
              children: (
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {formatValue(h.previousValue, historyData.valueType, historyData.unit)} → {formatValue(h.newValue, historyData.valueType, historyData.unit)}
                    {h.city && (
                      <Tag color="blue" style={{ marginLeft: 8, fontSize: '11px' }}>
                        {h.city.toUpperCase()}
                      </Tag>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {dayjs(h.modifiedAt).format('DD/MM/YYYY HH:mm')}
                    {h.modifiedBy && ` par ${h.modifiedBy.firstName || ''} ${h.modifiedBy.lastName || ''}`}
                  </div>
                  {h.reason && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <em>"{h.reason}"</em>
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            Aucun historique de modification
          </div>
        )}
      </Modal>
    </>
  );
};

export default Settings;
